import { useCreateCheckoutIntent } from '@sunrise-ui/api-client';
import { useCheckoutModalStore } from '@sunrise-ui/primitives';
import { useCheckoutFormStore } from '../stores/useCheckoutFormStore';
import { generateIdempotencyKey } from '../utils/idempotency';

export const usePrepareOrder = () => {
  const { mutateAsync: createCheckoutIntent, isPending: isPreparingOrder } =
    useCreateCheckoutIntent();

  const prepareOrder = async () => {
    const { eventId } = useCheckoutModalStore.getState();
    const { sessionId, attendee, ticketQuantities, appliedPromo } =
      useCheckoutFormStore.getState();

    if (!eventId || !attendee) {
      return { isSuccess: false, error: 'Missing information' };
    }

    try {
      const assignedTickets = [
        {
          first_name: attendee.firstName,
          last_name: attendee.lastName,
          email: attendee.email,
          phone_number: `+1${attendee.phoneNumber}`,
          ticket_type_id: attendee.ticketTypeId,
          sms_consent: attendee.smsConsent,
        },
      ];

      const unassignedTickets = Array.from(ticketQuantities.entries())
        .map(([ticketTypeId, quantity]) => {
          const unassignedQuantity =
            ticketTypeId === attendee.ticketTypeId ? quantity - 1 : quantity;

          return {
            ticket_type_id: ticketTypeId,
            quantity: unassignedQuantity,
          };
        })
        .filter((ticket) => ticket.quantity > 0);

      // Generate deterministic idempotency key from order data
      // Sort ticketQuantities (from Map) for consistent hashing
      const sortedTicketQuantities = Array.from(
        ticketQuantities.entries(),
      ).sort(([aId], [bId]) => aId.localeCompare(bId));

      const idempotencyKey = await generateIdempotencyKey({
        sessionId,
        eventId,
        attendee,
        ticketQuantities: sortedTicketQuantities,
        promoCode: appliedPromo?.discount_code,
      });

      const { checkout_id, client_secret, status } = await createCheckoutIntent(
        {
          createCheckoutIntentInput: {
            discount_code: appliedPromo?.discount_code,
            event_id: eventId,
            tickets: assignedTickets,
            unassigned_tickets: unassignedTickets,
            purchaser_email: attendee.email,
          },
          idempotencyKey,
        },
      );

      return {
        isSuccess: true,
        checkoutId: checkout_id,
        clientSecret: client_secret,
        status,
      };
    } catch (error) {
      return {
        isSuccess: false,
        error: error instanceof Error ? error.message : 'An error occurred',
      };
    }
  };

  return {
    prepareOrder,
    isPreparingOrder,
  };
};
